import { useCallback } from 'react'
import { isMobile } from 'react-device-detect'
import TagManager, { DataLayerArgs } from 'react-gtm-module'
import { styled } from 'styled-components'
import { deleteCart } from '../../api/cart'
import { BtnCartAllDelete } from '../../components/Button'
import { useAppContext } from '../../components/provider/AppProvider'
import { useCartContext } from '../../components/provider/CartProvider'
import { useCommonContext } from '../../components/provider/CommonProvider'
import { handlePostMessage, sendSetAppsflyerEvent } from '../../js/postMessage'
import {
  CLEAR_TOTAL_CART_PRICE_INFO,
  CLEAR_USER_CREDIT,
  SET_INVALID_ORDER_VARIATION_MAP,
  SET_ORDER_PRODUCT_MAP,
  SET_ORDER_VARIATION_MAP,
  SET_SELECT_ORDER_VARIATION_MAP,
  TOGGLE_INVALID,
} from './CartGroupProvider'
import useCartGroup from './useCartGroup'

export default function CartGroupHeader() {
  const { isAppView } = useAppContext()
  const { hasAccessToken } = useCommonContext()

  const { setAddCart } = useCartContext()
  const {
    isInvalid,
    orderVariationMap,
    invalidOrderVariationMap,
    selectOrderVariationMap,
    totalCartPriceInfo,
    dispatch,
    createGTMItems,
  } = useCartGroup()

  const onClickCartAllSelecte = useCallback(() => {
    if (Object.keys(selectOrderVariationMap).length === Object.keys(orderVariationMap).length) {
      dispatch({ type: SET_SELECT_ORDER_VARIATION_MAP, payload: {} })
      dispatch({ type: CLEAR_USER_CREDIT })

      return
    }

    dispatch({
      type: SET_SELECT_ORDER_VARIATION_MAP,
      payload: orderVariationMap,
    })
  }, [orderVariationMap, selectOrderVariationMap])

  const onClickCartAllDelete = useCallback(async () => {
    let targetOrderVariationMap = orderVariationMap

    if (isInvalid) {
      targetOrderVariationMap = invalidOrderVariationMap
    }

    if (Object.keys(targetOrderVariationMap).length === 0) {
      return alert('장바구니에 선택된 상품이 없습니다.')
    }

    if (!confirm(`${isInvalid ? '주문 불가 상품' : '주문 가능 상품'}이 모두 삭제됩니다.`)) return
    const orderVariationProductNos = Object.values(targetOrderVariationMap).map(
      (product: any) => `${product.productNo}`
    )
    const orderVariationCartNos = Object.values(targetOrderVariationMap).map((variation: any) => variation.cartNo)

    if (!hasAccessToken) {
      handlePostMessage({
        actionType: 'deleteCartApp',
        params: {
          mobile: isMobile ? 'mobile' : 'pc',
          productId: orderVariationProductNos,
        },
      })

      if (isInvalid) {
        dispatch({ type: SET_INVALID_ORDER_VARIATION_MAP, payload: {} })
      } else {
        dispatch({ type: SET_ORDER_PRODUCT_MAP, payload: {} })
        dispatch({ type: SET_ORDER_VARIATION_MAP, payload: {} })
        dispatch({ type: SET_SELECT_ORDER_VARIATION_MAP, payload: {} })
      }

      dispatch({ type: CLEAR_USER_CREDIT })
      dispatch({ type: CLEAR_TOTAL_CART_PRICE_INFO })
      dispatch({ type: TOGGLE_INVALID, payload: false })

      window.sessionStorage.setItem('guestCart', JSON.stringify([]))

      setAddCart((prev) => !prev)
    } else {
      try {
        const { data } = await deleteCart(orderVariationCartNos.join(','))

        if (data) {
          window.sessionStorage.setItem('cart', JSON.stringify([]))
          handlePostMessage({
            actionType: 'deleteCartApp',
            params: {
              mobile: isMobile ? 'mobile' : 'pc',
              productId: orderVariationProductNos,
            },
          })

          const gtmItems = await createGTMItems(Object.values(targetOrderVariationMap))

          const gtmDeleteCartItem: DataLayerArgs = {
            dataLayer: {
              event: 'remove_from_cart',
              ecommerce: {
                currency: 'KRW',
                value: totalCartPriceInfo?.buyAmt,
                items: gtmItems,
              },
            },
          }

          if (isAppView) {
            sendSetAppsflyerEvent({
              eventName: 'remove_from_cart',
              eventType: {
                currency: 'KRW',
                value: totalCartPriceInfo?.buyAmt,
                items: gtmItems,
              },
            })
          } else {
            TagManager.dataLayer(gtmDeleteCartItem)
          }

          if (isInvalid) {
            dispatch({ type: SET_INVALID_ORDER_VARIATION_MAP, payload: {} })
          } else {
            dispatch({ type: SET_ORDER_PRODUCT_MAP, payload: {} })
            dispatch({ type: SET_ORDER_VARIATION_MAP, payload: {} })
            dispatch({ type: SET_SELECT_ORDER_VARIATION_MAP, payload: {} })
          }

          dispatch({ type: CLEAR_USER_CREDIT })
          dispatch({ type: CLEAR_TOTAL_CART_PRICE_INFO })
          dispatch({ type: TOGGLE_INVALID, payload: false })

          window.localStorage.setItem('cartlist', JSON.stringify([]))
        }

        setAddCart((prev) => !prev)
      } catch (error) {
        console.error('장바구니 전체삭제 실패', error)
      }
    }
  }, [isInvalid, hasAccessToken, orderVariationMap, invalidOrderVariationMap, totalCartPriceInfo])

  const onClickCartItemTab = useCallback(
    (flag: boolean) => {
      dispatch({ type: TOGGLE_INVALID, payload: flag })
    },
    [dispatch]
  )

  return (
    <>
      <CartHeader>
        <div className='cart_item_left'>
          <span className='cart_item_count'>
            <strong>{Object.keys(orderVariationMap).length + Object.keys(invalidOrderVariationMap).length}</strong>개의
            상품
          </span>
          {!isInvalid && (
            <BtnToggleSelecte onClick={onClickCartAllSelecte}>
              <span>
                {Object.keys(selectOrderVariationMap).length === Object.keys(orderVariationMap).length
                  ? '선택해제'
                  : '전체선택'}
              </span>
            </BtnToggleSelecte>
          )}
        </div>

        <BtnCartAllDelete onClick={onClickCartAllDelete}>
          <img src={`/images/icon_delete.png`} alt='전체삭제' />
          <div className='allDelete'>전체삭제</div>
        </BtnCartAllDelete>
      </CartHeader>
      <CartItemTabs>
        <button
          type='button'
          className={`tab_button ${!isInvalid ? 'active' : ''}`}
          onClick={() => onClickCartItemTab(false)}
        >
          주문 가능 상품 ({Object.keys(orderVariationMap).length})
        </button>
        <button
          type='button'
          className={`tab_button ${isInvalid ? 'active' : ''}`}
          onClick={() => onClickCartItemTab(true)}
        >
          주문 불가 상품 ({Object.keys(invalidOrderVariationMap).length})
        </button>
      </CartItemTabs>
    </>
  )
}

export const CartHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 20px;
  border-bottom: 1px solid var(--colGray3);
  @media screen and (min-width: 768px) {
    font-size: 16px;
    padding: 12px 0;
  }
  .cart_item_left {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .cart_item_count {
    color: var(--colGray6);
    font-size: 16px;
    line-height: 1.5;
    font-weight: 500;
    > strong {
      font-weight: 500;
      color: var(--colBlack);
    }
  }
`

export const BtnToggleSelecte = styled.button`
  outline: none;
  background: none;
  border: none;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  &:before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 4px;
    margin-top: -4px;
    border-left: 1px solid var(--colBlack);
    border-bottom: 1px solid var(--colBlack);
    transform: rotate(-45deg);
  }
  > span {
    font-weight: 500;
    font-size: 14px;
    color: var(--colBlack);
  }
`

export const CartItemTabs = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 20px;
  border-bottom: 1px solid var(--colGray3);
  @media screen and (min-width: 768px) {
    padding: 0;
  }
  .tab_button {
    flex: 1;
    outline: none;
    background: none;
    border: none;
    position: relative;
    padding: 12px 0;
    font-size: 14px;
    color: var(--colGray6);
    cursor: pointer;
    &.active {
      font-weight: 600;
      color: var(--colBlack);
      &:after {
        display: block;
      }
    }
    &:after {
      content: '';
      display: none;
      width: 100%;
      height: 1px;
      position: absolute;
      bottom: 0;
      background-color: var(--colBlack);
    }
  }
`
